import { useRef, useState, useEffect } from 'react';
import { useRouter } from 'next/router';

import { useDebouncedState } from '@react-hookz/web';
import { useAutoCompleteValues } from '../../../HomePage/Layouts/queries/useAutoCompleteValues';

import { useURLStateManagement } from '../../../../components/SearchPage/hooks/useURLStateManagement/useURLStateManagement';
import { useFiltersState } from '../../../../features/filters/Filters.context';

import { pushToDataLayer } from '../../../../services/gtmService';
import { asString } from '../../../../utils/query-parameters';
import { MAKE_MODEL_SEO_SUPPLEMENTARY_FILTER_TYPES } from '../../../../features/filters/Filters.constants';
import {
  formatPathParameters,
  getFilterPathParameterData,
} from '../../../../helpers/seo/searchParams';
import { mapMakeModelQuery } from '../../../../components/SearchPage/features/makeModel/MakeModel.map';

import type { IHandleSelectSearchItem } from '../../../../components/Toolkit/SearchModal/BaseSearch';
import type {
  IAutoCompleteSuggestion,
  TAutoCompleteSearchRequestMakeModelFilters,
} from '../../../../types';

import type { KeywordSearchProps as KeywordSearchArgs } from '../../../../components/SearchPage/components/KeywordSearch/KeywordSearch.typed';

import { media, matchMedia } from '../../../../helpers/breakpoints';
import { PAGE } from '../../../../helpers/pages';

const useKeywordSearch = (args: KeywordSearchArgs) => {
  const { sectionName } = args;

  const searchRef = useRef<HTMLDivElement>(null);
  const [autoSuggestItems, setAutoSuggestItems] =
    useState<Array<IAutoCompleteSuggestion>>();

  const { query, push } = useRouter();
  const { section, words, make } = query;
  const keywordQueryValue = asString(words) ?? '';
  const {
    currentSection,
    makePathParameter,
    modelPathParameter,
    SEOFilterValue,
  } = formatPathParameters(section);

  const { filtersData } = useFiltersState();

  const ALL_SECTIONS_VALUE = 'all';

  const { updateURL, updateMakeModels } = useURLStateManagement();

  const onClickSuggestion = (args: IHandleSelectSearchItem) => {
    const { autoSuggestedText, searchRequest } = args;

    const searchRequestMakeAndModel = searchRequest?.makeModelFilters?.[0];
    const [section] = searchRequest?.sections ?? [];

    if (searchRequestMakeAndModel) {
      return handleMakeModelSelection(
        searchRequestMakeAndModel,
        autoSuggestedText,
      );
    }

    if (searchRequest?.filters) {
      return handleFilterSelection(searchRequest?.filters, autoSuggestedText);
    }

    handleSearchTerms({ autoSuggestedText, section });

    pushToDataLayer({
      event: 'keyword_search',
      searchType: 'section_keyword_search',
      searchTerm: autoSuggestedText,
    });
  };

  const handleSearchTerms = (args: {
    autoSuggestedText: string;
    section: string;
  }) => {
    const { autoSuggestedText, section } = args;
    /*
     * If we are required to change section, we do not retain previously applied
     * filters since they may no longer be relevant.
     *
     * However, we still add the terms to be searched in the new section.
     */
    if (
      currentSection !== ALL_SECTIONS_VALUE &&
      section === ALL_SECTIONS_VALUE
    ) {
      push(
        {
          pathname: `/${ALL_SECTIONS_VALUE}`,
          query: {
            words: autoSuggestedText,
          },
        },
        undefined,
        {
          shallow: true,
        },
      );
    } else {
      updateURL({ queryParams: { words: autoSuggestedText } });
    }
  };

  const handleMakeModelSelection = (
    searchRequestMakeAndModel: TAutoCompleteSearchRequestMakeModelFilters,
    autoSuggestedText: string,
  ) => {
    const searchRequestMake = searchRequestMakeAndModel.make;

    const [searchRequestModel] = searchRequestMakeAndModel?.model
      ? [searchRequestMakeAndModel.model]
      : [];

    if (makePathParameter) {
      handleMakePathParameter(searchRequestMake, searchRequestModel);
    } else if (make) {
      const makeValue = Array.isArray(make) ? make : [make];

      handleMakeQueryValue(makeValue, searchRequestMake, searchRequestModel);
    } else {
      const makeModels = [
        {
          make: searchRequestMake,
          model: searchRequestModel ? [searchRequestModel] : undefined,
        },
      ];

      updateMakeModels({
        makeModels,
        makeModelEditable: `make_model_${makeModels.length - 1}`,
      });
    }

    pushToDataLayer({
      event: 'keyword_search',
      searchType: 'section_structured_search',
      searchTerm: autoSuggestedText,
    });
  };

  const handleMakePathParameter = (
    searchRequestMake?: string,
    searchRequestModel?: string,
  ) => {
    if (makePathParameter === searchRequestMake) {
      const model = [
        ...(modelPathParameter &&
        searchRequestModel &&
        searchRequestModel !== modelPathParameter
          ? [modelPathParameter]
          : []),
        ...(searchRequestModel ? [searchRequestModel] : []),
      ];

      const makeModels = [
        {
          make: searchRequestMake,
          model: model.length > 0 ? model : undefined,
        },
      ];

      updateMakeModels({
        makeModels,
        makeModelEditable: `make_model_${makeModels.length - 1}`,
      });
    } else {
      const makeModels = [
        {
          make: makePathParameter,
          model: modelPathParameter ? [modelPathParameter] : undefined,
        },
        {
          make: searchRequestMake,
          model: searchRequestModel ? [searchRequestModel] : undefined,
        },
      ];

      updateMakeModels({
        makeModels,
        makeModelEditable: `make_model_${makeModels.length - 1}`,
      });
    }
  };

  const handleMakeQueryValue = (
    makeValues: Array<string>,
    searchRequestMake?: string,
    searchRequestModel?: string,
  ) => {
    const includesMake = makeValues.some(
      (make) => searchRequestMake && make.startsWith(searchRequestMake),
    );

    const mappedMakeModels = makeValues.flatMap((make) => {
      return (
        mapMakeModelQueries(make, searchRequestMake, searchRequestModel) ?? []
      );
    });

    const newMake = {
      make: searchRequestMake,
      model: searchRequestModel ? [searchRequestModel] : undefined,
    };

    const makeModels = [
      ...mappedMakeModels,
      // If the search suggestion does not match any preexisting makes we add it here
      ...(!includesMake && searchRequestMake ? [newMake] : []),
    ];

    updateMakeModels({
      makeModels,
      makeModelEditable: `make_model_${makeModels.length - 1}`,
    });
  };

  const mapMakeModelQueries = (
    make: string,
    searchRequestMake?: string,
    searchRequestModel?: string,
  ) => {
    // Make query does not match suggestion so we map query as normal
    if (searchRequestMake && !make.includes(searchRequestMake)) {
      return mapMakeModelQuery(make);
    }
    // Make query does match suggestion but the model does not, so we update model data associated with this query value
    const mappedItem = mapMakeModelQuery(make);

    const updateModels =
      searchRequestModel && !mappedItem.model?.includes(searchRequestModel);

    return [
      {
        ...mappedItem,
        model: [
          ...(searchRequestModel ? mappedItem.model : []),
          ...(updateModels ? [searchRequestModel] : []),
        ],
      },
    ];
  };

  const handleFilterSelection = (
    searchRequestFilters: {
      name: string;
      values: Array<string>;
    }[],
    autoSuggestedText: string,
  ) => {
    const [filter] = searchRequestFilters;
    const { name, values } = filter;
    const [value] = values;

    const { formattedFilterName } = getFilterPathParameterData(
      filtersData,
      SEOFilterValue,
    );

    const addFilterPathParameter =
      MAKE_MODEL_SEO_SUPPLEMENTARY_FILTER_TYPES.includes(name) &&
      makePathParameter &&
      modelPathParameter &&
      !SEOFilterValue;

    if (addFilterPathParameter) {
      updateURL({ pathParam: value, queryParams: { [name]: '' } });
    } else if (formattedFilterName === name && SEOFilterValue) {
      updateURL({
        pathParam: '',
        queryParams: {
          [name]: [SEOFilterValue, value],
        },
      });
    } else if (query[name]) {
      const currentValue = query[name] ?? [];
      const formattedCurrentValue = Array.isArray(currentValue)
        ? currentValue
        : [currentValue];

      updateURL({
        queryParams: {
          [name]: [value, ...formattedCurrentValue],
        },
      });
    } else {
      const queryParams = { [name]: value };
      updateURL({ queryParams });
    }
    pushToDataLayer({
      event: 'keyword_search',
      searchType: 'section_structured_search',
      searchTerm: autoSuggestedText,
    });
  };

  const [searchTextDebounced, setSearchTextDebounced] = useDebouncedState(
    '',
    200,
  );

  const { data: autoCompleteData } = useAutoCompleteValues(
    searchTextDebounced,
    PAGE.SEARCH,
  );

  useEffect(() => {
    if (autoCompleteData && searchTextDebounced.length > 2) {
      const rightTextMuted = `In ${sectionName}`;
      const filteredData: IAutoCompleteSuggestion[] = [
        ...autoCompleteData.suggestions.map((suggestion) => {
          return {
            ...suggestion,
            rightTextMuted,
            leftText: suggestion.leftText ?? `${searchTextDebounced}`,
          };
        }),
      ];

      // We manually add search term to start if not suggested
      if (
        filteredData[0]?.leftText?.toLowerCase() !==
          searchTextDebounced.toLowerCase() &&
        currentSection
      ) {
        filteredData.unshift({
          url: '',
          leftText: searchTextDebounced,
          rightText: '',
          rightTextMuted,
          searchRequest: {
            terms: searchTextDebounced,
            sections: [currentSection],
          },
        });
      }

      /* We manually add global search for term to end of array
      if not in all sections */
      if (section !== ALL_SECTIONS_VALUE) {
        filteredData.push({
          url: `/all?words=${searchTextDebounced}`,
          rightText: 'In All Sections',
          leftText: searchTextDebounced,
          searchRequest: {
            terms: searchTextDebounced,
            sections: [ALL_SECTIONS_VALUE],
          },
        });
      }

      setAutoSuggestItems(filteredData);
    } else if (searchTextDebounced.length <= 2) {
      setAutoSuggestItems(undefined);
    }
  }, [
    autoCompleteData,
    searchTextDebounced,
    sectionName,
    currentSection,
    section,
  ]);

  const onSearch = (words: string) => {
    updateURL({
      queryParams: {
        words,
      },
    });
  };

  const onFocus = () => {
    if (matchMedia(media.smallOnly) && searchRef?.current) {
      searchRef.current.scrollIntoView({
        behavior: 'smooth',
      });
    }
  };

  const onClose = () => setAutoSuggestItems(undefined);

  return {
    onClickSuggestion,
    onSearch,
    keywordQueryValue,
    handleSearchInput: setSearchTextDebounced,
    onFocus,
    searchRef,
    autoSuggestItems,
    onClose,
  };
};

export { useKeywordSearch };
